import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, throwError, forkJoin } from 'rxjs';

import { Page, queryPaginatedPost } from '../models/page';
import { AuthBaseService } from './auth-base.service';
import { UnpagedResult } from '../helper/dto/unpaged-result.dto';
import { UserDto } from '@/helper/dto/user.dto';
import { UserReadDto } from '@/helper/dto/user/read-user.dto';
import { BasicUserDto } from '@/helper/dto/user/basic-user.dto';
import { SettingDto } from '@/helper/dto/setting.dto';
import { FileUploadDto } from '@/helper/dto/file-upload.dto';
import { environment } from 'src/environments/environment';
import { RoleDto } from '@/helper/dto/role.dto';
import { PermissionDto } from '@/helper/dto/permission.dto';
import { CustomerDto } from '@/helper/dto/customer/customer.dto';
import { CustomerWithCountsDto } from '@/helper/dto/customer/customer.dto';
import { ProductTemplateDto } from '@/helper/dto/product-template/product-template.dto';
import { ProductDto, ProductReadDto } from '@/helper/dto/product-template/product.dto';
import { InvoiceDto, InvoiceReadDto } from '@/helper/dto/invoice/invoice.dto';
import { ProductQuantityChangeDto } from '@/helper/dto/product-template/product-quantity-change.dto';
import { RequestForApprovalDto, RequestForApprovalReadDto } from '@/helper/dto/request-for-approval/request-for-approval.dto';

@Injectable({ providedIn: 'root' })
export class ApiService extends AuthBaseService {
    someApiCall(id: string): Observable<any> {
        return this.http.get<any>(this.apiUrl('v1/some-api-call'), this.getOptions());
    }

    listSettingsUnpaged(): Observable<SettingDto[]> {
        return this.http.get<SettingDto[]>(this.apiUrl('v1/setting/unpaged'), this.getOptions());
    }

    deleteFile(fileId: string): Observable<FileUploadDto> {
        return this.http.delete<FileUploadDto>(this.apiUrl('v1/file-upload/' + fileId), this.getOptions());
    }

    getSetting(idOrKey: string): Observable<SettingDto> {
        return this.http.get<SettingDto>(this.apiUrl('v1/setting/' + idOrKey), this.getOptions());
    }

    uploadFile(file: File, isLogo: boolean = false): Observable<FileUploadDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<FileUploadDto>(this.apiUrl(`v1/file-upload?isLogo=${isLogo}`), formData, {
            headers: new HttpHeaders({
                Authorization: 'Bearer ' + this.token()
            })
        });
    }

    uploadFilePublic(file: File, isLogo: boolean = false): Observable<FileUploadDto> {
        const formData = new FormData();
        formData.append('file', file);
        return this.http.post<FileUploadDto>(this.apiUrl(`v1/file-upload/public?isLogo=${isLogo}`), formData, {
            headers: new HttpHeaders({
                Authorization: 'Bearer ' + this.token()
            })
        });
    }

    downloadFile(file: FileUploadDto, preview: boolean = false): Observable<any> {
        return file.isPublic ? this.serveFilePublic(file, preview) : this.serveFile(file._id!, preview);
    }

    resolveFilePath(file: FileUploadDto, preview: boolean = false): string {
        if (file.isPublic) {
            const path = (preview ? file.previewPath : file.path).replace(/^uploads-public[/\\]/, '');
            return this.apiUrl('v1/public-uploads/' + path.replaceAll('\\', '/'));
        }
        return this.apiUrl('v1/file-upload/serve/' + file._id + '?preview=' + preview);
    }

    serveFile(fileId: string, preview: boolean = false): Observable<any> {
        return this.http.get(this.apiUrl('v1/file-upload/serve/' + fileId + '?preview=' + preview), {
            responseType: 'arraybuffer',
            headers: new HttpHeaders({
                Authorization: 'Bearer ' + this.token()
            })
        });
    }

    serveFilePublic(file: FileUploadDto, preview: boolean = false): Observable<any> {
        const path = (preview ? file.previewPath : file.path).replace(/^uploads-public[/\\]/, '');
        return this.http.get(this.apiUrl('v1/public-uploads/' + path.replaceAll('\\', '/')), {
            responseType: 'arraybuffer'
        });
    }

    getMailRender(template: string, content: string): Observable<any> {
        return this.http.post(
            this.apiUrl(`v1/setting/generate-mail-preview`),
            { template, content },
            {
                headers: new HttpHeaders({
                    'Access-Control-Allow-Origin': environment.api_endpoint,
                    Authorization: 'Bearer ' + this.token()
                }),
                responseType: 'arraybuffer'
            }
        );
    }

    createSetting(setting: SettingDto): Observable<SettingDto> {
        return this.http.post<SettingDto>(this.apiUrl('v1/setting'), setting, this.getOptions());
    }

    updateSetting(id: string, setting: SettingDto): Observable<SettingDto> {
        return this.http.patch<SettingDto>(this.apiUrl('v1/setting/' + id), setting, this.getOptions());
    }

    getAllUsersUnpaged(): Observable<UnpagedResult<UserDto>> {
        return this.http.get<UnpagedResult<UserDto>>(this.apiUrl('v1/user/'), this.getOptions());
    }

    searchUsers(urlOrFilter?: object): Observable<Page<UserReadDto>> {
        const uOb = queryPaginatedPost<UserReadDto>(this.http, this.apiUrl('v1/user/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<UserReadDto>(uOb);
        return page;
    }

    getUserById(userId: string): Observable<UserReadDto> {
        return this.http.get<UserReadDto>(this.apiUrl(`v1/user/${userId}`), this.getOptions());
    }

    createUser(userDto: UserDto): Observable<UserDto> {
        return this.http.post<UserDto>(this.apiUrl('v1/user'), userDto, this.getOptions());
    }

    updateUser(userId: string, userDto: UserDto): Observable<UserDto> {
        return this.http.patch<UserDto>(this.apiUrl(`v1/user/${userId}`), userDto, this.getOptions());
    }

    deleteUser(userId: string): Observable<UserDto> {
        return this.http.delete<UserDto>(this.apiUrl(`v1/user/${userId}`), this.getOptions());
    }

    getUserProfile(): Observable<UserReadDto> {
        return this.http.get<UserReadDto>(this.apiUrl('v1/user/my/profile'), this.getOptions());
    }

    updateUserProfile(userDto: UserDto): Observable<UserDto> {
        return this.http.patch<UserDto>(this.apiUrl('v1/user/my/profile'), userDto, this.getOptions());
    }

    getCurrentUser(): Observable<UserReadDto | BasicUserDto> {
        return this.http.get<UserReadDto | BasicUserDto>(this.apiUrl('v1/user/current'), this.getOptions());
    }

    updateCurrentUser(userDto: UserDto): Observable<UserDto> {
        return this.http.patch<UserDto>(this.apiUrl(`v1/user/current`), userDto, this.getOptions());
    }

    // Role API methods
    searchRoles(urlOrFilter?: object): Observable<Page<RoleDto>> {
        const uOb = queryPaginatedPost<RoleDto>(this.http, this.apiUrl('v1/role/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<RoleDto>(uOb);
        return page;
    }

    getUserRoles(userId: string): Observable<RoleDto[]> {
        return this.http.get<RoleDto[]>(this.apiUrl(`v1/role/user/${userId}`), this.getOptions());
    }

    getCurrentUserRoles(): Observable<RoleDto[]> {
        return this.http.get<RoleDto[]>(this.apiUrl('v1/role/my'), this.getOptions());
    }

    getRole(roleId: string): Observable<RoleDto> {
        return this.http.get<RoleDto>(this.apiUrl(`v1/role/${roleId}`), this.getOptions());
    }

    createRole(roleDto: RoleDto): Observable<RoleDto> {
        return this.http.post<RoleDto>(this.apiUrl('v1/role'), roleDto, this.getOptions());
    }

    updateRole(roleId: string, roleDto: RoleDto): Observable<RoleDto> {
        return this.http.patch<RoleDto>(this.apiUrl(`v1/role/${roleId}`), roleDto, this.getOptions());
    }

    deleteRole(roleId: string): Observable<RoleDto> {
        return this.http.delete<RoleDto>(this.apiUrl(`v1/role/${roleId}`), this.getOptions());
    }

    cloneRole(roleId: string): Observable<RoleDto> {
        return this.http.patch<RoleDto>(this.apiUrl(`v1/role/${roleId}/clone`), {}, this.getOptions());
    }

    getRolesUnpaged(): Observable<RoleDto[]> {
        return this.http.post<RoleDto[]>(this.apiUrl('v1/role/unpaged'), {}, this.getOptions());
    }

    getRolesUnpagedPopulated(): Observable<RoleDto[]> {
        return this.http.post<RoleDto[]>(this.apiUrl('v1/role/unpaged/populated'), {}, this.getOptions());
    }

    // Permission API methods
    getPermissionsUnpaged(): Observable<PermissionDto[]> {
        return this.http.get<PermissionDto[]>(this.apiUrl('v1/permission/unpaged'), this.getOptions());
    }

    createCustomer(customerDto: CustomerDto): Observable<CustomerDto> {
        return this.http.post<CustomerDto>(this.apiUrl('v1/customer'), customerDto, this.getOptions());
    }

    searchCustomers(urlOrFilter?: object): Observable<Page<CustomerWithCountsDto>> {
        const uOb = queryPaginatedPost<CustomerWithCountsDto>(this.http, this.apiUrl('v1/customer/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<CustomerWithCountsDto>(uOb);
        return page;
    }

    getAllCustomers(): Observable<CustomerDto[]> {
        return this.http.get<CustomerDto[]>(this.apiUrl("v1/customer"), this.getOptions());
    }

    getCustomerById(customerId: string): Observable<CustomerDto> {
        return this.http.get<CustomerDto>(this.apiUrl(`v1/customer/${customerId}`), this.getOptions());
    }

    updateCustomer(customerId: string, customerDto: CustomerDto): Observable<CustomerDto> {
        return this.http.patch<CustomerDto>(this.apiUrl(`v1/customer/${customerId}`), customerDto, this.getOptions());
    }

    syncCustomers(): Observable<CustomerDto[]> {
        return this.http.get<CustomerDto[]>(this.apiUrl("v1/customer/sync"), this.getOptions());
    }

    syncProxmoxProduct(productId: string): Observable<void> {
        return this.http.patch<void>(this.apiUrl(`v1/product/provider-update/proxmox/${productId}`), {}, this.getOptions());
    }

    syncMailstoreProduct(productId: string): Observable<void> {
        return this.http.patch<void>(this.apiUrl(`v1/product/provider-update/mailstore/${productId}`), {}, this.getOptions());
    }

    syncNinjaProduct(): Observable<void> {
        return this.http.patch<void>(this.apiUrl("v1/product/provider-update/ninja"), {}, this.getOptions());
    }

    syncSentinelOneProduct(): Observable<void> {
        return this.http.patch<void>(this.apiUrl("v1/product/provider-update/sentinelone"), {}, this.getOptions());
    }

    deleteCustomer(customerId: string): Observable<CustomerDto> {
        return this.http.delete<CustomerDto>(this.apiUrl(`v1/customer/${customerId}`), this.getOptions());
    }

    createProductTemplate(productTemplateDto: ProductTemplateDto): Observable<ProductTemplateDto> {
        return this.http.post<ProductTemplateDto>(this.apiUrl('v1/product-template'), productTemplateDto, this.getOptions());
    }

    getProductTemplatesUnpaged(): Observable<ProductTemplateDto[]> {
        return this.http.get<ProductTemplateDto[]>(this.apiUrl('v1/product-template/unpaged'), this.getOptions());
    }

    searchProductTemplates(urlOrFilter?: object): Observable<Page<ProductTemplateDto>> {
        const uOb = queryPaginatedPost<ProductTemplateDto>(this.http, this.apiUrl('v1/product-template/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<ProductTemplateDto>(uOb);
        return page;
    }

    getProductTemplateById(productTemplateId: string): Observable<ProductTemplateDto> {
        return this.http.get<ProductTemplateDto>(this.apiUrl(`v1/product-template/${productTemplateId}`), this.getOptions());
    }

    updateProductTemplate(productTemplateId: string, productTemplateDto: ProductTemplateDto): Observable<ProductTemplateDto> {
        return this.http.patch<ProductTemplateDto>(this.apiUrl(`v1/product-template/${productTemplateId}`), productTemplateDto, this.getOptions());
    }

    deleteProductTemplate(productTemplateId: string): Observable<ProductTemplateDto> {
        return this.http.delete<ProductTemplateDto>(this.apiUrl(`v1/product-template/${productTemplateId}`), this.getOptions());
    }

    createProduct(productDto: ProductDto): Observable<ProductDto> {
        return this.http.post<ProductDto>(this.apiUrl('v1/product'), productDto, this.getOptions());
    }

    getProductsUnpaged(): Observable<ProductReadDto[]> {
        return this.http.get<ProductReadDto[]>(this.apiUrl('v1/product/unpaged'), this.getOptions());
    }

    getProductsByCustomerId(customerId: string): Observable<ProductReadDto[]> {
        return this.http.get<ProductReadDto[]>(this.apiUrl(`v1/product/customer/${customerId}`), this.getOptions());
    }

    searchProducts(urlOrFilter?: object): Observable<Page<ProductReadDto>> {
        const uOb = queryPaginatedPost<ProductReadDto>(this.http, this.apiUrl('v1/product/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<ProductReadDto>(uOb);
        return page;
    }

    getProductById(productId: string): Observable<ProductReadDto> {
        return this.http.get<ProductReadDto>(this.apiUrl(`v1/product/${productId}`), this.getOptions());
    }

    updateProduct(productId: string, productDto: Partial<ProductDto>): Observable<Partial<ProductReadDto>> {
        return this.http.patch<Partial<ProductReadDto>>(this.apiUrl(`v1/product/${productId}`), productDto, this.getOptions());
    }

    deleteProduct(productId: string): Observable<ProductDto> {
        return this.http.delete<ProductDto>(this.apiUrl(`v1/product/${productId}`), this.getOptions());
    }

    searchProductQuantityChanges(urlOrFilter?: object): Observable<Page<ProductQuantityChangeDto>> {
        const uOb = queryPaginatedPost<ProductQuantityChangeDto>(this.http, this.apiUrl('v1/product-quantity-change/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<ProductQuantityChangeDto>(uOb);
        return page;
    }

    getProductQuantityChangeById(productId: string): Observable<ProductQuantityChangeDto> {
        return this.http.get<ProductQuantityChangeDto>(this.apiUrl(`v1/product-quantity-change/${productId}`), this.getOptions());
    }

    getProductQuantityChangeByProductId(productId: string): Observable<ProductQuantityChangeDto[]> {
        return this.http.get<ProductQuantityChangeDto[]>(
            this.apiUrl(
                `v1/product-quantity-change/find-by-product/${productId}`
            ),
            this.getOptions()
        );
    }

    createInvoice(invoiceDto: InvoiceDto): Observable<InvoiceDto> {
        return this.http.post<InvoiceDto>(this.apiUrl('v1/invoice'), invoiceDto, this.getOptions());
    }

    confirmAndSendInvoiceToSevDesk(id: string): Observable<InvoiceReadDto> {
        return this.http.get<InvoiceReadDto>(this.apiUrl(`v1/invoice/create-in-sevdesk/${id}`), this.getOptions());
    }

    getInvoicesUnpaged(): Observable<InvoiceDto[]> {
        return this.http.get<InvoiceDto[]>(this.apiUrl('v1/invoice/unpaged'), this.getOptions());
    }

    searchInvoices(urlOrFilter?: object, state?: "outlines" | "confirmed"): Observable<Page<InvoiceDto>> {
        const uOb = queryPaginatedPost<InvoiceDto>(this.http, this.apiUrl(`v1/invoice/search?state=${state}`), urlOrFilter, this.getOptions());
        const page = this.toPage<InvoiceDto>(uOb);
        return page;
    }

    getInvoiceById(invoiceId: string): Observable<InvoiceReadDto> {
        return this.http.get<InvoiceReadDto>(this.apiUrl(`v1/invoice/${invoiceId}`), this.getOptions());
    }

    updateInvoice(invoiceId: string, invoiceDto: InvoiceDto): Observable<InvoiceDto> {
        return this.http.patch<InvoiceDto>(this.apiUrl(`v1/invoice/${invoiceId}`), invoiceDto, this.getOptions());
    }

    deleteInvoice(invoiceId: string): Observable<InvoiceDto> {
        return this.http.delete<InvoiceDto>(this.apiUrl(`v1/invoice/${invoiceId}`), this.getOptions());
    }

    createRequestForApproval(requestForApprovalDto: RequestForApprovalDto): Observable<RequestForApprovalDto> {
        return this.http.post<RequestForApprovalDto>(this.apiUrl('v1/request-for-approval'), requestForApprovalDto, this.getOptions());
    }

    getRequestsForApprovalUnpaged(): Observable<RequestForApprovalReadDto[]> {
        return this.http.get<RequestForApprovalReadDto[]>(this.apiUrl('v1/request-for-approval/unpaged'), this.getOptions());
    }

    searchRequestsForApproval(urlOrFilter?: object): Observable<Page<RequestForApprovalReadDto>> {
        const uOb = queryPaginatedPost<RequestForApprovalReadDto>(this.http, this.apiUrl('v1/request-for-approval/search'), urlOrFilter, this.getOptions());
        const page = this.toPage<RequestForApprovalReadDto>(uOb);
        return page;
    }

    getRequestForApprovalById(requestForApprovalId: string): Observable<RequestForApprovalReadDto> {
        return this.http.get<RequestForApprovalReadDto>(this.apiUrl(`v1/request-for-approval/${requestForApprovalId}`), this.getOptions());
    }

    updateRequestForApproval(requestForApprovalId: string, requestForApprovalDto: RequestForApprovalDto): Observable<RequestForApprovalDto> {
        return this.http.patch<RequestForApprovalDto>(this.apiUrl(`v1/request-for-approval/${requestForApprovalId}`), requestForApprovalDto, this.getOptions());
    }

    approveRequestForApprovalById(requestForApprovalId: string): Observable<RequestForApprovalReadDto> {
        return this.http.get<RequestForApprovalReadDto>(this.apiUrl(`v1/request-for-approval/approve/${requestForApprovalId}`), this.getOptions());
    }

    declineRequestForApprovalById(requestForApprovalId: string, declineReason: string): Observable<RequestForApprovalReadDto> {
        return this.http.patch<RequestForApprovalReadDto>(this.apiUrl(`v1/request-for-approval/decline/${requestForApprovalId}`), { declineReason }, this.getOptions());
    }

    deleteRequestForApproval(requestForApprovalId: string): Observable<RequestForApprovalDto> {
        return this.http.delete<RequestForApprovalDto>(this.apiUrl(`v1/request-for-approval/${requestForApprovalId}`), this.getOptions());
    }

    uploadFiles(files: File[], isPublic: boolean = false): Observable<any[]> {
        if (!files || files.length === 0)
            return new Observable((obs) => {
                obs.next([]);
                obs.complete();
            });

        const url = this.apiUrl(`v1/file-upload${isPublic ? '/public' : ''}`);

        const requests = files.map((f) => {
            const formData = new FormData();
            formData.append('file', f, f.name);
            return this.http.post<any>(url, formData, this.getOptions());
        });

        return forkJoin(requests);
    }

    toPage<T>(observable: any): Observable<Page<T>> {
        return observable.pipe(
            map((pageData: any) => {
                const page = new Page();
                if (pageData == null) {
                    return throwError(() => new Error('Page data is null'));
                }

                page.total = pageData.totalCount ? pageData.totalCount : 0;
                page.currentPage = pageData.page ? pageData.page : 1;
                page.pageSize = pageData.size ? pageData.size : 25;
                page.data = pageData.data ? (pageData.data as T[]) : [];
                page.duration = pageData.duration ? pageData.duration : 0;
                return page;
            })
        );
    }

    getSettingByKey(key: string): Observable<{ valueBoolean?: boolean; valueString?: string; valueNumber?: number }> {
        return this.http.get<{ valueBoolean?: boolean; valueString?: string; valueNumber?: number }>(this.apiUrl(`v1/setting/by-key/${key}`), this.getOptions());
    }
}
